import React, { useState } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { PlusIcon, Calendar, Clock, CheckCircleIcon, Users, Video, ExternalLink, XIcon, EditIcon, Trash2Icon, ChevronRight } from 'lucide-react';
import { coffeeData, Coffee } from '../components/coffee/coffeeData';
import CreateCoffeeModal from '../components/coffee/CreateCoffeeModal';
import { useNavigate, Link } from 'react-router-dom';
// Move formatting functions outside components to make them accessible
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
};
const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('it-IT', {
    hour: '2-digit',
    minute: '2-digit'
  });
};
interface EventCardProps {
  event: Coffee;
  onClick: () => void;
  onEdit?: (coffee: Coffee) => void;
  onDelete?: (coffee: Coffee) => void;
}
const EventCard: React.FC<EventCardProps> = ({
  event,
  onClick,
  onEdit,
  onDelete
}) => {
  const [showRequestsModal, setShowRequestsModal] = useState(false);
  const now = new Date();
  const coffeeDate = new Date(event.date);
  const isPast = coffeeDate < now;
  const isJoined = event.participants.some(p => p.id === 'current-user');
  const hasParticipated = isPast && isJoined;
  const isHost = event.host.id === 'current-user';
  const hasPendingRequests = event.pendingRequests && event.pendingRequests.length > 0;
  // Platform logos
  const getPlatformLogo = (platform: string) => {
    switch (platform) {
      case 'Google Meet':
        return 'https://uploadthingy.s3.us-west-1.amazonaws.com/hawwkzWf3TnSE8Q31Hrx5c/images.png';
      case 'Microsoft Teams':
        return 'https://upload.wikimedia.org/wikipedia/commons/c/c9/Microsoft_Office_Teams_%282018%E2%80%93present%29.svg';
      case 'Zoom':
        return 'https://uploadthingy.s3.us-west-1.amazonaws.com/a6M2ckhEkmcVgkXUVkifgg/zoom_logo_icon_147196.png';
      default:
        return null;
    }
  };
  const handleCardClick = (e: React.MouseEvent) => {
    // Only trigger the main card click if we're not clicking on the menu or its buttons
    if (!(e.target as HTMLElement).closest('.coffee-card-menu') && !(e.target as HTMLElement).closest('.coffee-card-action-btn')) {
      onClick();
    }
  };
  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(event);
    }
  };
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      if (window.confirm('Sei sicuro di voler eliminare questo evento?')) {
        onDelete(event);
      }
    }
  };
  const handleAcceptRequest = (requestId: string) => {
    console.log(`Accepted request ${requestId}`);
    // In a real app, you would make an API call here
    // For now, just close the modal
    setShowRequestsModal(false);
  };
  const handleRejectRequest = (requestId: string) => {
    console.log(`Rejected request ${requestId}`);
    // In a real app, you would make an API call here
    // For now, just close the modal
    setShowRequestsModal(false);
  };
  return <div className="bg-white rounded-xl border border-gray-200 
      overflow-hidden h-full flex flex-col hover:shadow-md transition-shadow cursor-pointer relative" onClick={handleCardClick} data-id="element-2643">
      <div className="p-5 flex-1 flex flex-col" data-id="element-2644">
        <div className="flex items-start mb-4" data-id="element-2645">
          <div className="w-12 h-12 rounded-full overflow-hidden mr-3 flex-shrink-0 border-2 border-custom-green" data-id="element-2646">
            <img src={event.host.avatar} alt={event.host.name} className="w-full h-full object-cover" data-id="element-2647" />
          </div>
          <div className="flex-1" data-id="element-2648">
            <h3 className="text-base font-semibold text-gray-800 mb-1" data-id="element-2649">
              {event.title}
            </h3>
            <p className="text-sm text-gray-500 mb-2" data-id="element-2650">Organizzato da te</p>
            {/* Show pending requests badge next to the title */}
            {hasPendingRequests && !isPast && <div className="inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium" data-id="element-2651">
                <Users size={12} className="mr-1" data-id="element-2652" />
                {event.pendingRequests.length} richieste in attesa
              </div>}
          </div>
          {isHost && !isPast && <div className="coffee-card-menu ml-2" data-id="element-2653">
              <div className="flex space-x-1" data-id="element-2654">
                <button onClick={handleEditClick} className="p-1 rounded-full hover:bg-gray-100" title="Modifica evento" data-id="element-2655">
                  <EditIcon size={14} className="text-gray-500" data-id="element-2656" />
                </button>
                <button onClick={handleDeleteClick} className="p-1 rounded-full hover:bg-gray-100" title="Elimina evento" data-id="element-2657">
                  <Trash2Icon size={14} className="text-gray-500" data-id="element-2658" />
                </button>
              </div>
            </div>}
        </div>

        <div className="flex flex-wrap gap-2 mb-4" data-id="element-2659">
          {event.topics.map((topic, index) => <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs" data-id="element-2660">
              {topic}
            </span>)}
          {hasParticipated && <span className="px-2 py-1 bg-custom-green/10 text-custom-green rounded-full text-xs flex items-center" data-id="element-2661">
              <CheckCircleIcon size={12} className="mr-1" data-id="element-2662" />
              Hai partecipato
            </span>}
          {isHost && !isPast && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center" data-id="element-2663">
              Sei l'host
            </span>}
        </div>

        <div className="flex items-center text-sm text-gray-500 mb-1" data-id="element-2664">
          <Calendar size={16} className="mr-2" data-id="element-2665" />
          {formatDate(event.date)}
        </div>
        <div className="flex items-center text-sm text-gray-500 mb-1" data-id="element-2666">
          <Clock size={16} className="mr-2" data-id="element-2667" />
          {formatTime(event.date)} - {formatTime(event.endTime)}
        </div>
        <div className="flex items-center text-sm text-gray-500 mb-4" data-id="element-2668">
          {getPlatformLogo(event.platform) ? <img src={getPlatformLogo(event.platform)} alt={event.platform} className="w-5 h-5 mr-2 object-contain" data-id="element-2669" /> : <Video size={16} className="mr-2" data-id="element-2670" />}
          {event.platform}
        </div>

        <p className="text-sm text-gray-600 mb-4" data-id="element-2671">{event.description}</p>

        {/* Participants and pending requests section */}
        <div className="space-y-3 mb-4" data-id="element-2672">
          <div className="flex items-center" data-id="element-2673">
            <Users size={16} className="text-gray-500 mr-2" data-id="element-2674" />
            <span className="text-sm text-gray-500" data-id="element-2675">
              {event.participants.length === 0 ? 'Nessun partecipante' : `${event.participants.length} partecipant${event.participants.length === 1 ? 'e' : 'i'}`}
            </span>
          </div>

          {/* Pending requests section - more prominent */}
          {hasPendingRequests && !isPast && <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-3" data-id="element-2676">
              <div className="flex items-center justify-between" data-id="element-2677">
                <div className="flex items-center" data-id="element-2678">
                  <Users size={16} className="text-yellow-600 mr-2" data-id="element-2679" />
                  <span className="text-sm font-medium text-yellow-700" data-id="element-2680">
                    {event.pendingRequests.length} richieste di partecipazione
                    in attesa
                  </span>
                </div>
                <button onClick={e => {
              e.stopPropagation();
              setShowRequestsModal(true);
            }} className="coffee-card-action-btn text-xs px-3 py-1 bg-white text-yellow-700 border border-yellow-300 rounded-md hover:bg-yellow-100 transition-colors" data-id="element-2681">
                  Gestisci
                </button>
              </div>
              {/* Preview of pending requests */}
              <div className="mt-2 flex -space-x-2 overflow-hidden" data-id="element-2682">
                {event.pendingRequests.slice(0, 3).map(request => <div key={request.id} className="inline-block h-6 w-6 rounded-full ring-2 ring-white" data-id="element-2683">
                    <img src={request.avatar} alt={request.name} className="h-full w-full object-cover rounded-full" data-id="element-2684" />
                  </div>)}
                {event.pendingRequests.length > 3 && <div className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-200 text-xs font-medium text-gray-800 ring-2 ring-white" data-id="element-2685">
                    +{event.pendingRequests.length - 3}
                  </div>}
              </div>
            </div>}
        </div>

        <div className="mt-auto flex flex-col gap-2" data-id="element-2686">
          {isPast ? hasParticipated ? <button className="w-full flex items-center justify-center py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors" data-id="element-2687">
                Dettagli
              </button> : <button disabled className="w-full py-2 bg-gray-200 text-gray-500 rounded-lg font-medium" data-id="element-2688">
                Terminato
              </button> : <>
              <button className="w-full flex items-center justify-center py-2 bg-custom-green text-white rounded-lg hover:bg-custom-green/90 transition-colors" data-id="element-2689">
                <ExternalLink size={16} className="mr-2" data-id="element-2690" />
                Partecipa
              </button>
              {isHost && <div className="flex gap-2" data-id="element-2691">
                  <button onClick={handleEditClick} className="coffee-card-action-btn flex-1 py-2 px-4 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-sm" data-id="element-2692">
                    Modifica
                  </button>
                  <button onClick={handleDeleteClick} className="coffee-card-action-btn flex-1 py-2 px-4 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm" data-id="element-2693">
                    Elimina
                  </button>
                </div>}
            </>}
        </div>
      </div>

      {/* Pending Requests Modal */}
      {showRequestsModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={e => {
      e.stopPropagation();
      setShowRequestsModal(false);
    }} data-id="element-2694">
          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-2695">
            <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-2696">
              Richieste di partecipazione
            </h3>
            <div className="space-y-3" data-id="element-2697">
              {event.pendingRequests && event.pendingRequests.map(request => <div key={request.id} className="flex items-center justify-between border border-gray-200 rounded-lg p-3" data-id="element-2698">
                    <div className="flex items-center" data-id="element-2699">
                      <div className="w-10 h-10 rounded-full overflow-hidden mr-3" data-id="element-2700">
                        <img src={request.avatar} alt={request.name} className="w-full h-full object-cover" data-id="element-2701" />
                      </div>
                      <div data-id="element-2702">
                        <p className="font-medium text-gray-800" data-id="element-2703">
                          {request.name}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2" data-id="element-2704">
                      <button onClick={() => handleRejectRequest(request.id)} className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-full" data-id="element-2705">
                        <XIcon size={16} data-id="element-2706" />
                      </button>
                      <button onClick={() => handleAcceptRequest(request.id)} className="p-2 text-gray-500 hover:text-custom-green hover:bg-custom-green/10 rounded-full" data-id="element-2707">
                        <CheckCircleIcon size={16} data-id="element-2708" />
                      </button>
                    </div>
                  </div>)}
            </div>
            <div className="mt-6 flex justify-end" data-id="element-2709">
              <button onClick={() => setShowRequestsModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors" data-id="element-2710">
                Chiudi
              </button>
            </div>
          </div>
        </div>}
    </div>;
};
const MyCoffeePage = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [coffeeToEdit, setCoffeeToEdit] = useState<Coffee | null>(null);
  const [localCoffeeData, setLocalCoffeeData] = useState([...coffeeData]);
  const navigate = useNavigate();
  // Helper function to determine if an event is past and user participated
  const isPastAndParticipated = (event: Coffee) => {
    const now = new Date();
    const eventDate = new Date(event.date);
    const isPast = eventDate < now;
    const isParticipant = event.participants.some(p => p.id === 'current-user');
    return isPast && isParticipant;
  };
  // Filtrare gli eventi creati dall'utente corrente
  const myHostedEvents = localCoffeeData.filter(coffee => coffee.host.id === 'current-user' || coffee.host.id.startsWith('admin') && coffee.participants.some(p => p.id === 'current-user'));
  const handleCoffeeClick = (coffeeId: string) => {
    navigate(`/coffee/${coffeeId}`);
  };
  const handleEditCoffee = (coffee: Coffee) => {
    setCoffeeToEdit(coffee);
  };
  const handleDeleteCoffee = (coffee: Coffee) => {
    // In a real app, this would make an API call
    // For now, we'll just update the local state
    const updatedData = localCoffeeData.filter(c => c.id !== coffee.id);
    setLocalCoffeeData(updatedData);
    // Show a success message
    alert('Evento eliminato con successo!');
  };
  return <div className="flex h-screen bg-gray-100" data-id="element-2711">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 md:hidden ${sidebarOpen ? 'block' : 'hidden'}`} data-id="element-2712">
        {/* Sidebar backdrop */}
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} data-id="element-2713"></div>
        {/* Sidebar */}
        <div className="relative flex-1 flex flex-col w-2/3 h-full bg-black" data-id="element-2714">
          <div className="absolute top-0 right-0 -mr-12 pt-2" data-id="element-2715">
            <button type="button" className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white" onClick={() => setSidebarOpen(false)} data-id="element-2716">
              <span className="sr-only" data-id="element-2717">Close sidebar</span>
              <XIcon className="h-6 w-6 text-white" aria-hidden="true" data-id="element-2718" />
            </button>
          </div>
          <div className="flex-1 h-full overflow-y-auto" data-id="element-2719">
            <Sidebar activePage="community" data-id="element-2720" />
          </div>
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:flex-shrink-0" data-id="element-2721">
        <Sidebar activePage="community" data-id="element-2722" />
      </div>

      <div className="flex flex-col flex-1 overflow-hidden" data-id="element-2723">
        <Topbar onMenuClick={() => setSidebarOpen(true)} data-id="element-2724" />

        <main className="flex-1 overflow-y-auto p-4 md:p-6 pt-0 md:pt-0 bg-gray-100" data-id="element-2725">
          <div className="pt-4 md:pt-6 max-w-6xl mx-auto" data-id="element-2726">
            {/* Breadcrumbs */}
            <nav className="flex mb-4" aria-label="Breadcrumb" data-id="element-2727">
              <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-2728">
                <li className="inline-flex items-center" data-id="element-2729">
                  <Link to="/coffee" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900" data-id="element-2730">
                    Eventi Greenback
                  </Link>
                </li>
                <li data-id="element-2731">
                  <div className="flex items-center" data-id="element-2732">
                    <ChevronRight size={16} className="text-gray-400" data-id="element-2733" />
                    <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2" data-id="element-2734">
                      I Miei Eventi
                    </span>
                  </div>
                </li>
              </ol>
            </nav>

            <div className="flex justify-between items-center mb-8" data-id="element-2735">
              <h1 className="text-2xl font-semibold text-gray-800" data-id="element-2736">
                I Miei Eventi
              </h1>
              <button onClick={() => setIsCreateModalOpen(true)} className="flex items-center px-4 py-2 bg-custom-green text-white rounded-md hover:bg-custom-green/90 transition-colors" data-id="element-2737">
                <PlusIcon size={18} className="mr-2" data-id="element-2738" />
                Nuovo Evento
              </button>
            </div>

            {myHostedEvents.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2739">
                {myHostedEvents.map((event, index) => {
              // Check if the event is in the past
              const eventDate = new Date(event.date);
              const now = new Date();
              const isPastEvent = eventDate < now;
              // Simulate different participant states for demo purposes
              const modifiedEvent = {
                ...event,
                host: {
                  ...event.host,
                  avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/4hFhikAPJ46vhrcKePNA5J/IMG_0014.jpg'
                },
                // Simulate different states based on index
                participants: index % 3 === 0 ? [] // No participants
                : index % 3 === 1 ? event.participants // With participants
                : [...event.participants],
                // Add pending requests ONLY for events that are not in the past
                pendingRequests: index % 3 === 2 && !isPastEvent ? [{
                  id: 'pending-1',
                  name: 'Marco Bianchi',
                  avatar: 'https://images.unsplash.com/photo-1599566150163-29194dcaad36?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
                }, {
                  id: 'pending-2',
                  name: 'Giulia Verdi',
                  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
                }] : []
              };
              return <EventCard key={event.id} event={modifiedEvent} onClick={() => handleCoffeeClick(event.id)} onEdit={handleEditCoffee} onDelete={handleDeleteCoffee} data-id="element-2740" />;
            })}
              </div> : <div className="bg-gray-50 rounded-lg p-6 text-center" data-id="element-2741">
                <p className="text-gray-600" data-id="element-2742">
                  Non hai ancora creato eventi. Crea il tuo primo evento
                  Greenback!
                </p>
              </div>}
          </div>
        </main>
      </div>

      <CreateCoffeeModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} data-id="element-2743" />
    </div>;
};
export default MyCoffeePage;